import React, { useRef, useState } from "react";

/**
 * List editor for choice-field options: edit / add / remove / drag-reorder.
 */
export default function ESourceOptionEditor({
  options = [],
  onChange,
  disabled = false,
  idPrefix = "opt",
  maxItems = 50,
  minItems = 0,
  hint = "One option per row. Drag to reorder.",
}) {
  const list = Array.isArray(options) ? options.map(String) : [];
  const dragIndexRef = useRef(null);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const commit = (next) => {
    onChange?.(next.map(String));
  };

  const updateAt = (index, value) => {
    const next = [...list];
    next[index] = value;
    commit(next);
  };

  const removeAt = (index) => {
    if (list.length <= minItems) return;
    commit(list.filter((_, i) => i !== index));
  };

  const reorder = (fromIndex, toIndex) => {
    if (
      fromIndex == null ||
      toIndex == null ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= list.length ||
      toIndex >= list.length
    ) {
      return;
    }
    const next = [...list];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    commit(next);
  };

  const clearDrag = () => {
    dragIndexRef.current = null;
    setDragFromIndex(null);
    setDragOverIndex(null);
  };

  const add = () => {
    if (list.length >= maxItems) return;
    commit([...list, `Option ${list.length + 1}`]);
  };

  return (
    <div className="esource-option-editor">
      <ul className="esource-option-editor__list list-unstyled mb-2">
        {list.map((opt, index) => (
          <li
            key={`${idPrefix}-${index}`}
            className={`esource-option-editor__row${
              dragOverIndex === index ? " is-drag-over" : ""
            }${dragFromIndex === index ? " is-dragging" : ""}`}
            onDragOver={(e) => {
              if (disabled || dragIndexRef.current == null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (dragOverIndex !== index) setDragOverIndex(index);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget)) return;
              setDragOverIndex((cur) => (cur === index ? null : cur));
            }}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndexRef.current;
              clearDrag();
              if (disabled) return;
              reorder(from, index);
            }}
          >
            <span
              className="esource-option-editor__grip"
              title="Drag to reorder"
              aria-label="Drag to reorder"
              draggable={!disabled}
              onDragStart={(e) => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                dragIndexRef.current = index;
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(index));
                setDragFromIndex(index);
                setDragOverIndex(index);
              }}
              onDragEnd={clearDrag}
            >
              <i className="fas fa-grip-vertical" aria-hidden="true" />
            </span>
            <input
              className="form-control form-control-sm"
              value={opt}
              disabled={disabled}
              aria-label={`Option ${index + 1}`}
              onChange={(e) => updateAt(index, e.target.value)}
            />
            <div className="esource-option-editor__actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                title="Delete"
                disabled={disabled || list.length <= minItems}
                onClick={() => removeAt(index)}
              >
                <i className="fas fa-trash-alt" aria-hidden="true" />
              </button>
            </div>
          </li>
        ))}
      </ul>
      {!disabled && list.length < maxItems ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={add}
        >
          <i className="fas fa-plus me-1" aria-hidden="true" />
          Add option
        </button>
      ) : null}
      {hint ? <span className="form-text d-block mt-1">{hint}</span> : null}
    </div>
  );
}
